import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MatchPublic } from '@mondy/shared';
import { api } from '@/api';
import { Link } from 'react-router';
import { STAGE_LABELS, formatKickoff, matchLabel } from '@/lib/format';
import { cn } from '@/lib/cn';
import { AdminResultForm } from '@/components/admin/AdminResultForm';
import { AdminNav } from '@/components/admin/AdminNav';
import { Spinner } from '@/components/Spinner';

interface MatchListResponse {
  matches: MatchPublic[];
}

type FilterKey = 'needs-result' | 'live' | 'finished' | 'all';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'needs-result', label: 'Needs result' },
  { key: 'live', label: 'Live' },
  { key: 'finished', label: 'Finished' },
  { key: 'all', label: 'All' },
];

export function AdminMatchesPage() {
  const [filter, setFilter] = useState<FilterKey>('needs-result');

  const query = useQuery({
    queryKey: ['admin', 'matches', 'all'],
    queryFn: () => api.get<MatchListResponse>('/api/matches'),
    refetchInterval: 30_000,
  });

  const matches = query.data?.matches ?? [];
  const now = Date.now();
  const filtered = matches.filter((m) => {
    switch (filter) {
      case 'needs-result':
        return m.status !== 'finished' && new Date(m.kickoffUtc).getTime() < now;
      case 'live':
        return m.status === 'live';
      case 'finished':
        return m.status === 'finished';
      case 'all':
      default:
        return true;
    }
  });

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 md:p-8">
      <AdminNav />
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Admin · Matches</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Record full-time / ET / penalty scores. Sets the match to <code>manualOverride</code> so
          the football-data.org sync won't clobber it.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium',
              filter === f.key
                ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
                : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] text-[color:var(--color-fg-muted)]',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <Spinner fullScreen />
      ) : filtered.length === 0 ? (
        <p className="py-8 text-center text-sm text-[color:var(--color-fg-muted)]">
          No matches match this filter.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {filtered.map((m) => (
            <AdminMatchRow key={m.id} m={m} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AdminMatchRow({ m }: { m: MatchPublic }) {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { t1, t2 } = matchLabel(m);

  return (
    <li className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[11px] uppercase tracking-wide text-[color:var(--color-fg-muted)]">
            #{m.matchNumber} · {m.group ? `Group ${m.group}` : STAGE_LABELS[m.stage]}
          </span>
          <span className="truncate font-medium">
            {t1} <span className="text-[color:var(--color-fg-muted)]">vs</span> {t2}
          </span>
          <span className="text-xs text-[color:var(--color-fg-muted)]">
            {formatKickoff(m.kickoffUtc)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {m.result ? (
            <span className="rounded-md bg-[color:var(--color-bg)] px-2 py-1 text-sm tabular-nums">
              {m.result.fullTime.team1}–{m.result.fullTime.team2}
            </span>
          ) : null}
          <span
            className={cn(
              'text-sm transition',
              expanded && 'rotate-90',
            )}
            aria-hidden
          >
            ›
          </span>
        </div>
      </button>
      {expanded ? (
        <div className="border-t border-[color:var(--color-border)] p-4">
          {m.team1 && m.team2 ? (
            <AdminResultForm
              match={m}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['admin', 'matches'] });
                void queryClient.invalidateQueries({ queryKey: ['matches'] });
                void queryClient.invalidateQueries({ queryKey: ['match', m.id] });
              }}
            />
          ) : (
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              Teams are still placeholders (
              <Link to={`/matches/${m.id}`} className="underline">
                {m.team1Placeholder ?? '?'} vs {m.team2Placeholder ?? '?'}
              </Link>
              ). Result entry will be available once the bracket resolves.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}
